import { createContextualCan } from '@casl/react'
import type { ReactNode } from 'react'
import { createContext, useContext, useMemo } from 'react'

import type { AppAbility } from '@/libs/ability'
import { defineAbilitiesFor, defineAbilitiesFromPermissions } from '@/libs/ability'
import { useAppSelector } from '@/store/hooks'

const AbilityContext = createContext<AppAbility>({} as AppAbility)

export const Can = createContextualCan(AbilityContext.Consumer)

export function AbilityProvider({ children }: { children: ReactNode }) {
  const permissions = useAppSelector((state) => state.auth.permissions)

  const ability = useMemo((): AppAbility => {
    if (permissions && permissions.length > 0) {
      return defineAbilitiesFromPermissions(permissions)
    }
    // Fallback: no permissions yet (pre-login) — deny everything
    return defineAbilitiesFor('viewer')
  }, [permissions])

  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
}

export function useAbility() {
  const context = useContext(AbilityContext)
  if (!context) {
    throw new Error('useAbility must be used within an AbilityProvider')
  }
  return context
}
